/**
 * CLINIC-DAY-SPINE-V1 -- the leads-workspace "Find by phone" modal
 * (founder ruling 2026-08-06, R-CD-B1 under R-CD-R1 SEARCH-FIRST ONE
 * DOOR; model: lead-enquiry-model.ts).
 *
 * The door is SEARCH: the operator searches a phone against THE existing
 * patient-master search read (patient-wins resolution + the enquiry
 * glance are SERVER behavior -- FD-16; this modal invents no ordering).
 * HIT -> the existing records render and a human confirms (patient cards
 * carry the UMR badge -- R-CD-R2-L2: UMR presence IS the differentiator;
 * an enquiry card has none). MISS on a PHONE search -> exactly ONE
 * action, "Create new enquiry", phone pre-filled and read-only. The
 * created LEAD feeds hop 2 (claim + book) unchanged.
 *
 * SECURITY: presentation only -- the server fences the search admission,
 * the origin token (R-CD-R2-L4), the write audience, the clinic scope,
 * and the source vocabulary.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  createOperatorEnquiry,
  fetchOperatorEnquiryConfig,
  fetchPatientMasterSearch,
  type OperatorEnquiryConfigWire,
} from '@/dashboard/api';
import { AcButton, ModalShell } from '@/dashboard/ui';
import {
  RecordCard,
  type SearchResultCardView,
} from '@/dashboard/patients/RegisterPatientSteps';
import {
  patientRecordHref,
  type EnquiryGlanceCard,
  type PatientSearchResponse,
} from '@/dashboard/patients/patient-master-view';
import { humanizeSource } from '@/walkin/ExistingLeadMatches';
import {
  LEAD_CREATE_ACTION,
  LEAD_CREATE_TITLE,
  LEAD_NONE_OF_THESE,
  LEAD_OPEN_ENQUIRY_ACTION,
  LEAD_PHONE_FIRST_HINT,
  LEAD_SEARCH_BUTTON,
  LEAD_SEARCH_PLACEHOLDER,
  LEAD_SEARCH_TITLE,
  LEAD_SEARCH_UNAVAILABLE,
  leadEnquiryEvidence,
  leadSearchNoRecordLine,
  validateLeadEnquiryForm,
} from './lead-enquiry-model';

function makeRequestId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `enq-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
  }
}

export default function LeadEnquirySearchModal({
  onClose,
}: {
  readonly onClose: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [result, setResult] = useState<PatientSearchResponse | null>(null);
  const [searchedQuery, setSearchedQuery] = useState('');
  const [noneOfThese, setNoneOfThese] = useState(false);

  const [config, setConfig] = useState<OperatorEnquiryConfigWire | null>(null);
  const [name, setName] = useState('');
  const [treatmentType, setTreatmentType] = useState('');
  const [source, setSource] = useState('');
  const [clinicId, setClinicId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<readonly string[]>([]);
  const requestId = useMemo(() => makeRequestId(), []);

  useEffect(() => {
    let alive = true;
    fetchOperatorEnquiryConfig()
      .then((wire) => {
        if (!alive) return;
        setConfig(wire);
        if (wire.clinics.length === 1) setClinicId(wire.clinics[0]!.clinicId);
      })
      .catch(() => {
        // The form validates on submit; a failed config read leaves the
        // create door disabled with the honest search-unavailable copy.
        if (alive) setConfig(null);
      });
    return () => {
      alive = false;
    };
  }, []);

  const runSearch = useCallback(async () => {
    const q = query.trim();
    if (q.length < 2) return;
    setSearching(true);
    setSearchFailed(false);
    setNoneOfThese(false);
    try {
      const res = await fetchPatientMasterSearch(q);
      setResult(res);
      setSearchedQuery(q);
    } catch {
      setResult(null);
      setSearchFailed(true);
      setSearchedQuery(q);
    } finally {
      setSearching(false);
    }
  }, [query]);

  const candidates: readonly SearchResultCardView[] = useMemo(
    () =>
      result === null
        ? []
        : result.candidates.map((c) => ({
            patientId: c.patientId,
            umr: c.umr,
            fullName: c.fullName,
            gender: c.gender,
            ageYears: c.ageYears,
            city: c.city,
            registeredClinicId: c.registeredClinicId,
            registeredClinicLabel: c.registeredClinicLabel,
          })),
    [result],
  );
  const enquiries: readonly EnquiryGlanceCard[] = result?.enquiries ?? [];

  const evidence = leadEnquiryEvidence({
    searched: result !== null || searchFailed,
    searchFailed,
    resultCount: candidates.length + enquiries.length,
    noneOfThese,
    searchMode: result?.searchMode ?? null,
  });
  const createOpen = evidence === 'no-match' || evidence === 'none-of-these';

  const submitEnquiry = useCallback(async () => {
    const form = { phone: searchedQuery, name, treatmentType, source, clinicId };
    const errors = validateLeadEnquiryForm(form);
    if (errors.length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors([]);
    setSaving(true);
    try {
      const created = await createOperatorEnquiry({
        phone: searchedQuery,
        name: name.trim(),
        treatmentType,
        source,
        clinicId: clinicId as number,
        requestId,
      });
      onClose();
      navigate(`/dashboard/leads/${created.safeLeadId}`);
    } catch (err) {
      setFormErrors([
        err instanceof Error && err.message !== ''
          ? err.message
          : 'The enquiry could not be saved. Nothing was recorded.',
      ]);
    } finally {
      setSaving(false);
    }
  }, [
    searchedQuery,
    name,
    treatmentType,
    source,
    clinicId,
    requestId,
    navigate,
    onClose,
  ]);

  return (
    <ModalShell
      title={LEAD_SEARCH_TITLE}
      onClose={onClose}
      testId="lead-enquiry-search-modal"
    >
      <div className="ac-wizard" data-testid="lead-enquiry-search-content">
        <div className="ac-wizard__card ac-wizard__search-card">
          <input
            type="search"
            placeholder={LEAD_SEARCH_PLACEHOLDER}
            aria-label={LEAD_SEARCH_TITLE}
            data-testid="lead-enquiry-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void runSearch();
            }}
          />
          <AcButton
            variant="primary"
            disabled={searching || query.trim().length < 2}
            onClick={() => void runSearch()}
            data-testid="lead-enquiry-search-button"
          >
            {LEAD_SEARCH_BUTTON}
          </AcButton>
        </div>

        {evidence === 'search-failed' ? (
          <p className="ac-dim" data-testid="lead-enquiry-search-failed">
            {LEAD_SEARCH_UNAVAILABLE}
          </p>
        ) : null}

        {candidates.length > 0 ? (
          <ul className="ac-record-card-list" data-testid="lead-enquiry-patients">
            {candidates.map((card) => (
              <RecordCard
                key={card.patientId}
                card={card}
                busy={saving}
                facts={result?.deskFacts?.[card.patientId] ?? null}
                onSelect={(picked) => {
                  onClose();
                  navigate(patientRecordHref(picked.patientId));
                }}
              />
            ))}
          </ul>
        ) : null}

        {enquiries.length > 0 ? (
          <ul className="ac-record-card-list" data-testid="lead-enquiry-enquiries">
            {enquiries.map((card) => (
              <li className="ac-record-card" key={card.leadId}>
                <dl className="ac-record-card__facts">
                  <div className="ac-record-card__fact">
                    <dt>Name</dt>
                    <dd>{card.name ?? 'Not captured'}</dd>
                  </div>
                  {card.source !== '' ? (
                    <div className="ac-record-card__fact">
                      <dt>Source</dt>
                      <dd>{humanizeSource(card.source)}</dd>
                    </div>
                  ) : null}
                  {card.clinicName !== null ? (
                    <div className="ac-record-card__fact">
                      <dt>Clinic</dt>
                      <dd>{card.clinicName}</dd>
                    </div>
                  ) : null}
                </dl>
                <AcButton
                  size="sm"
                  variant="primary"
                  disabled={saving}
                  onClick={() => {
                    onClose();
                    navigate(`/dashboard/leads/${card.leadId}`);
                  }}
                  data-testid={`lead-enquiry-open-${card.leadId}`}
                >
                  {LEAD_OPEN_ENQUIRY_ACTION}
                </AcButton>
              </li>
            ))}
          </ul>
        ) : null}

        {candidates.length + enquiries.length > 0 && !noneOfThese ? (
          <AcButton
            variant="ghost"
            disabled={saving}
            onClick={() => setNoneOfThese(true)}
            data-testid="lead-enquiry-none-of-these"
          >
            {LEAD_NONE_OF_THESE}
          </AcButton>
        ) : null}

        {evidence === 'phone-first-hint' ? (
          <p className="ac-dim" data-testid="lead-enquiry-phone-first">
            {LEAD_PHONE_FIRST_HINT}
          </p>
        ) : null}

        {createOpen ? (
          <div className="ac-wizard__card" data-testid="lead-enquiry-create">
            {evidence === 'no-match' ? (
              <p data-testid="lead-enquiry-no-record">
                {leadSearchNoRecordLine(searchedQuery)}
              </p>
            ) : null}
            <h3>{LEAD_CREATE_TITLE}</h3>
            <label className="ac-field">
              <span>Phone</span>
              <input
                type="tel"
                value={searchedQuery}
                readOnly
                data-testid="lead-enquiry-phone"
              />
            </label>
            <label className="ac-field">
              <span>Name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="lead-enquiry-name"
              />
            </label>
            <label className="ac-field">
              <span>What they asked about</span>
              <select
                value={treatmentType}
                onChange={(e) => setTreatmentType(e.target.value)}
                data-testid="lead-enquiry-bucket"
              >
                <option value="">Pick one</option>
                {(config?.treatmentTypes ?? []).map((bucket) => (
                  <option key={bucket} value={bucket}>
                    {bucket}
                  </option>
                ))}
              </select>
            </label>
            <label className="ac-field">
              <span>Source / channel</span>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                data-testid="lead-enquiry-source"
              >
                <option value="">Pick one</option>
                {(config?.sources ?? []).map((token) => (
                  <option key={token} value={token}>
                    {humanizeSource(token)}
                  </option>
                ))}
              </select>
            </label>
            {(config?.clinics.length ?? 0) > 1 ? (
              <label className="ac-field">
                <span>Clinic</span>
                <select
                  value={clinicId === null ? '' : String(clinicId)}
                  onChange={(e) =>
                    setClinicId(
                      e.target.value === '' ? null : Number(e.target.value),
                    )
                  }
                  data-testid="lead-enquiry-clinic"
                >
                  <option value="">Pick one</option>
                  {(config?.clinics ?? []).map((c) => (
                    <option key={c.clinicId} value={String(c.clinicId)}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {formErrors.length > 0 ? (
              <ul className="ac-form-errors" data-testid="lead-enquiry-errors">
                {formErrors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            ) : null}
            <AcButton
              variant="primary"
              disabled={saving || config === null}
              onClick={() => void submitEnquiry()}
              data-testid="lead-enquiry-create-submit"
            >
              {LEAD_CREATE_ACTION}
            </AcButton>
          </div>
        ) : null}
      </div>
    </ModalShell>
  );
}
